/** @jsxImportSource @emotion/react */
import React from 'react';
import styled from '@emotion/styled';
import * as font from '@constant/font';
import * as color from '@constant/color';
import * as size from '@constant/size';
import * as design from '@constant/design';
import { useTypedSelector } from '@hook/common/useStore';
import Gap from '@component/common/gap';
import TextBlock from 'component/common/block/text-block';
import ImageTextBlock from 'component/common/block/image-text-block';
import AuthBox from 'component/auth/auth-box';
import TextInput from '@component/common/input/text-input';
import { createAuthLogoImage } from '@util/image';
import { createErrorText } from '@util/string';
import { useAuthenticationCode, useAuthInput, useAuthPage } from '@hook/auth/useAuth';
import { SERVICE } from '@config/environment';
import { EAuthType } from '@datatype/type';

const AuthCode: React.FC = () => {
  const errorCode = useTypedSelector((state) => state.rootReducer?.authReducer?.errorCode) ?? null;

  const { issueAuthenticationCode, authenticateCode, codeState } = useAuthenticationCode();
  const { email, code } = useAuthInput();
  const { openSignupPage } = useAuthPage();

  const handleCodeSendButtonClick = async () => await issueAuthenticationCode({ service: SERVICE, email: email.value });
  const handleCodeAuthenticateButtonClick = async () => {
    const result = await authenticateCode(email.value, code.value);
    if (!result) return;
    openSignupPage(EAuthType.INHOUSE, email.value);
  };

  return (
    <AuthBox design={design.AUTH_BOX_DESIGN}>
      <ImageTextBlock
        src={createAuthLogoImage()}
        alt='otterbrick'
        text='회원가입'
        design={design.AUTH_LOGO_DESIGN}
      />
      <Gap height={size.MARGIN_2} />
      {codeState === 'init' && (
        <>
          <TextInput
            name={'email'}
            placeholder={'이메일'}
            design={design.AUTH_INPUT_DESIGN}
            onChange={email.handler}
            value={email.value}
          />
          <TextBlock
            text={createErrorText(errorCode)}
            design={{
              ...design.ERROR_TEXT_DESIGN,
              box: { ...design.ERROR_TEXT_DESIGN, height: size.ERROR_TEXT_HEIGHT_1 },
            }}
          />
          <TextBlock
            text={'인증번호 보내기'}
            design={design.createAuthButtonDesign()}
            click={{ handler: handleCodeSendButtonClick, mode: 'throttle', delay: 3000 }}
          />
        </>
      )}
      {codeState === 'sent' && (
        <>
          <TextBlock
            design={{
              text: { size: 'medium' },
              box: { width: size.AUTH_BLOCK_WIDTH, height: size.AUTH_BLOCK_HEIGHT },
            }}
            text={email.value}
          />
          <TextInput
            name={'code'}
            placeholder={'인증번호'}
            design={{ ...design.AUTH_INPUT_DESIGN, box: { ...design.AUTH_INPUT_DESIGN.box, alignX: 'center' } }}
            onChange={code.handler}
            value={code.value}
          />
          <TextBlock
            text={createErrorText(errorCode)}
            design={{
              ...design.ERROR_TEXT_DESIGN,
              box: { ...design.ERROR_TEXT_DESIGN, height: size.ERROR_TEXT_HEIGHT_1 },
            }}
          />
          <TextBlock
            text={'인증하기'}
            design={design.createAuthButtonDesign()}
            click={{ handler: handleCodeAuthenticateButtonClick, mode: 'throttle', delay: 3000 }}
          />
          <Gap height={size.MARGIN_1} />
          <TextBlock
            text={'인증번호 다시 보내기'}
            design={design.createAuthButtonDesign()}
            click={{ handler: handleCodeSendButtonClick, mode: 'throttle', delay: 3000 }}
          />
        </>
      )}
    </AuthBox>
  );
};

export default AuthCode;
